import { useState, useEffect, memo } from "react";
import styled, { keyframes, css } from "styled-components";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  Clock,
  Coins,
  X,
  User,
  Shield,
  ShieldOff,
  Ban,
  CheckCircle,
  AlertTriangle,
  History,
  Calendar,
  Zap,
  Crown,
  User as UserIcon,
} from "lucide-react";
import {
  banUser,
  unbanUser,
  updateUserBalance,
  updateUserLifeTime,
} from "../../../api/user";
import { RootState } from "../../../redux/type";
import { demoteUser, promoteToAdmin } from "../../../api/admin";
import InlineEditor from "./InlineEditor";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

const ModalBackdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 20px;
  width: 100%;
  max-width: 500px;
  margin: 2rem auto;
  max-height: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 600px) {
    border-radius: 16px;
    margin: 1rem 0;
  }
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;

  @media (max-width: 480px) {
    padding: 1rem;
    gap: 0.75rem;
  }
`;

const UserAvatar = styled.div<{ $src?: string; $name: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ $src, $name }) =>
    $src
      ? `url(${$src}) center/cover`
      : `linear-gradient(135deg, ${
          $name.charCodeAt(0) % 2 === 0 ? "#ccff00" : "#00e0ff"
        }, ${$name.charCodeAt(0) % 2 === 0 ? "#a0ff00" : "#0099cc"})`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #fff;
  font-weight: bold;
  border: 2px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 1rem;
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h2`
  color: #fff;
  margin: 0 0 0.25rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UserId = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr; /* 2 columns */
  grid-auto-rows: 120px; /* fixed row height for alignment */
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(0, 0, 0, 0.2);
`;

// Fixed: Use css helper for conditional animations
const StatCard = styled.div<{ $critical?: boolean }>`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid
    ${(props) =>
      props.$critical ? "rgba(255, 77, 77, 0.3)" : "rgba(255, 255, 255, 0.1)"};
  display: flex;
  flex-direction: column;
  justify-content: center; /* vertically center all content */
  gap: 0.5rem;

  ${(props) =>
    props.$critical &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `}
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center; /* vertically align icon and label */
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const StatIcon = styled.div<{ $color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(props) => props.$color || "rgba(204, 255, 0, 0.1)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.$color ? props.$color : "#ccff00")};
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center; /* vertically align text and icon */
  gap: 0.25rem;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    padding: 0 1rem 1rem;
  }
`;

const ActionButton = styled(motion.button)<{ $variant?: "danger" | "success" }>`
  background: ${(props) => {
    switch (props.$variant) {
      case "danger":
        return "rgba(255, 77, 77, 0.1)";
      case "success":
        return "rgba(76, 175, 80, 0.1)";
      default:
        return "rgba(255, 255, 255, 0.05)";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$variant) {
        case "danger":
          return "rgba(255, 77, 77, 0.3)";
        case "success":
          return "rgba(76, 175, 80, 0.3)";
        default:
          return "rgba(255, 255, 255, 0.1)";
      }
    }};
  padding: 0.75rem;
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;

  &:hover {
    background: ${(props) => {
      switch (props.$variant) {
        case "danger":
          return "rgba(255, 77, 77, 0.2)";
        case "success":
          return "rgba(76, 175, 80, 0.2)";
        default:
          return "rgba(255, 255, 255, 0.1)";
      }
    }};
    transform: translateY(-1px);
  }
`;

const HistorySection = styled.div`
  margin-top: 1rem;
  padding: 0 1.25rem 1.5rem;
  overflow-y: auto;
  max-height: 45vh;

  @media (max-width: 500px) {
    max-height: 40vh;
    padding: 0 1rem 1rem;
  }
`;

const SectionTitle = styled.h3`
  color: #ccff00;
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HistoryList = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-height: 200px;
  overflow-y: auto;
`;

const HistoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const HistoryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HistoryDate = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
`;

const HistoryReason = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
`;

const HistoryPoints = styled.div<{ $positive: boolean }>`
  color: ${(props) => (props.$positive ? "#ccff00" : "#ff4d4d")};
  font-weight: 600;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span<{ $banned?: boolean; $admin?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${(props) => {
    if (props.$banned) return "rgba(255, 77, 77, 0.2)";
    if (props.$admin) return "rgba(0, 224, 255, 0.2)";
    return "rgba(76, 175, 80, 0.2)";
  }};
  color: ${(props) => {
    if (props.$banned) return "#ff4d4d";
    if (props.$admin) return "#00e0ff";
    return "#4CAF50";
  }};
`;

export interface User {
  uid: number | string;
  username: string;
  points: number;
  lifelineTime: number;
  createdAt: string;
  banned: boolean;
  banReason?: string;
  role: string;
  telegramId: number;
  profilePicture?: string;
  pointsHistory: {
    reason: string;
    date: Date;
    points: number;
    lifelineTime: number;
  }[];
}

const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours}h ${minutes}m`;
};

const calculateLifeTimeData = (user: User, currentTime: Date) => {
  const createdAt = new Date(user.createdAt);
  const lifelineTime = user.lifelineTime;
  const lifelineTimeMs = lifelineTime * 3600 * 1000;

  const remainingMs =
    lifelineTimeMs - (currentTime.getTime() - createdAt.getTime());
  const remainingSeconds = Math.max(Math.floor(remainingMs / 1000), 0);

  return {
    remainingTime: remainingMs,
    formattedTime: formatTime(remainingSeconds),
  };
};

// Live countdown component
const LiveTimer = memo(({ user }: { user: User }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000); // Update every second for live countdown

    return () => clearInterval(timer);
  }, []);

  const { formattedTime, remainingTime } = calculateLifeTimeData(user, now);
  const isCritical = remainingTime <= 3600 * 1000; // Less than 1 hour

  return (
    <StatCard $critical={isCritical}>
      <StatHeader>
        <StatIcon
          $color={
            isCritical ? "rgba(255, 77, 77, 0.1)" : "rgba(255, 193, 7, 0.1)"
          }
        >
          <Clock color={isCritical ? "#ff4d4d" : "#ffc107"} size={16} />
        </StatIcon>
        <StatLabel>Time Remaining</StatLabel>
      </StatHeader>
      <StatValue
        style={{ color: isCritical ? "#ff4d4d" : "#ffc107", fontSize: "1rem" }}
      >
        {formattedTime}
        {isCritical && <AlertTriangle size={14} />}
      </StatValue>
    </StatCard>
  );
});

export const EnhancedDetailsModal = ({
  user,
  onClose,
  onUserUpdate,
}: {
  user: User;
  onClose: () => void;
  onUserUpdate: (updatedUser: User) => void;
}) => {
  const currentAdmin = useSelector((state: RootState) => state.userData);

  const handleAction = async (action: string) => {
    try {
      switch (action) {
        case "ban":
          await banUser(user.uid, "Admin action", currentAdmin?.uid);
          onUserUpdate({ ...user, banned: true });
          toast.success("User banned");
          break;
        case "unban":
          await unbanUser(user.uid, currentAdmin?.uid);
          onUserUpdate({ ...user, banned: false });
          toast.success("User unbanned");
          break;
        case "promote":
          await promoteToAdmin(user.uid, currentAdmin?.uid);
          onUserUpdate({ ...user, role: "admin" });
          toast.success("User promoted");
          break;
        case "demote":
          await demoteUser(user.uid, currentAdmin?.uid);
          onUserUpdate({ ...user, role: "user" });
          toast.success("User demoted");
          break;
      }
    } catch (error) {
      toast.error(error.message || `Error performing ${action}`);
    }
  };

  const handleUpdateBalanceInline = async (newVal: number) => {
    try {
      await updateUserBalance(user.uid, newVal, currentAdmin?.uid);
      onUserUpdate({ ...user, points: newVal });
      toast.success("Balance updated");
    } catch (err) {
      toast.error("Failed to update balance");
    }
  };

  const handleUpdateLifetimeInline = async (newVal: number) => {
    try {
      await updateUserLifeTime(user.uid, newVal, currentAdmin?.uid);
      onUserUpdate({ ...user, lifelineTime: newVal });
      toast.success("Lifetime updated");
    } catch (err) {
      toast.error("Failed to update lifetime");
    }
  };

  const getStatusBadge = () => {
    if (user.banned) {
      return (
        <StatusBadge $banned>
          <Ban size={12} /> Banned
        </StatusBadge>
      );
    }
    if (user.role === "admin") {
      return (
        <StatusBadge $admin>
          <Crown size={12} /> Admin
        </StatusBadge>
      );
    }
    return (
      <StatusBadge>
        <CheckCircle size={12} /> Active
      </StatusBadge>
    );
  };

  return (
    <ModalBackdrop
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <UserAvatar $src={user.profilePicture} $name={user.username}>
            {!user.profilePicture && user.username.charAt(0).toUpperCase()}
          </UserAvatar>

          <UserInfo>
            <UserName>
              {user.username}
              {getStatusBadge()}
            </UserName>
            <UserId>
              <UserIcon size={12} />
              ID: {user.telegramId}
            </UserId>
          </UserInfo>

          <CloseButton onClick={onClose}>
            <X size={16} />
          </CloseButton>
        </ModalHeader>

        <StatsGrid>
          <StatCard>
            <StatHeader>
              <StatIcon>
                <Coins size={16} />
              </StatIcon>
              <StatLabel>XP Balance</StatLabel>
            </StatHeader>
            <StatValue>
              <InlineEditor
                value={user.points}
                onSave={handleUpdateBalanceInline}
              />
            </StatValue>
          </StatCard>

          <StatCard>
            <StatHeader>
              <StatIcon $color="rgba(0, 224, 255, 0.1)">
                <Zap color="#00e0ff" size={16} />
              </StatIcon>
              <StatLabel>Lifetime (Hrs)</StatLabel>
            </StatHeader>
            <StatValue>
              <InlineEditor
                value={user.lifelineTime}
                onSave={handleUpdateLifetimeInline}
              />
            </StatValue>
          </StatCard>

          <LiveTimer user={user} />

          <StatCard>
            <StatHeader>
              <StatIcon $color="rgba(255, 255, 255, 0.1)">
                <Calendar size={16} />
              </StatIcon>
              <StatLabel>Joined</StatLabel>
            </StatHeader>
            <StatValue style={{ fontSize: "0.9rem" }}>
              {new Date(user.createdAt).toLocaleDateString()}
            </StatValue>
          </StatCard>
        </StatsGrid>

        <ActionsGrid>
          {user.banned ? (
            <ActionButton
              $variant="success"
              onClick={() => handleAction("unban")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <CheckCircle size={16} />
              Unban
            </ActionButton>
          ) : (
            <ActionButton
              $variant="danger"
              onClick={() => handleAction("ban")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Ban size={16} />
              Ban
            </ActionButton>
          )}

          {user.role === "admin" ? (
            <ActionButton
              onClick={() => handleAction("demote")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ShieldOff size={16} />
              Demote
            </ActionButton>
          ) : (
            <ActionButton
              $variant="success"
              onClick={() => handleAction("promote")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Shield size={16} />
              Promote
            </ActionButton>
          )}
        </ActionsGrid>

        <HistorySection>
          <SectionTitle>
            <History size={16} />
            XP History
          </SectionTitle>
          <HistoryList>
            {user.pointsHistory && user.pointsHistory.length > 0 ? (
              [...user.pointsHistory].reverse().map((entry, index) => (
                <HistoryItem key={index}>
                  <HistoryInfo>
                    <HistoryDate>
                      {new Date(entry.date).toLocaleDateString()}
                    </HistoryDate>
                    <HistoryReason>{entry.reason || "No reason"}</HistoryReason>
                  </HistoryInfo>
                  <HistoryPoints $positive={entry.lifelineTime >= 0}>
                    {entry.lifelineTime > 0
                      ? `+${entry.lifelineTime} hrs`
                      : entry.lifelineTime}
                  </HistoryPoints>
                </HistoryItem>
              ))
            ) : (
              <EmptyState>
                <History size={24} />
                <div>No XP history</div>
              </EmptyState>
            )}
          </HistoryList>
        </HistorySection>
      </ModalContent>
    </ModalBackdrop>
  );
};
